import { useEffect, useRef, useCallback } from "react"
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { toast } from "react-toastify"
import { getCookie } from "../utils/cookies"

const getBaseUrl = () => {
  return process.env.REACT_APP_ENV && process.env.REACT_APP_ENV === "local"
    ? "http://localhost:5235"
    : "https://api.academetrics.com.br"
}

/**
 * Hook que estabelece uma conexão SignalR para receber notificações
 * em tempo real sobre importações processadas em background.
 *
 * @param {string} institutionId - ID da instituição para se juntar ao grupo
 * @param {function} onImportCompleted - Callback chamado quando uma importação finaliza
 */
const useImportNotifications = (institutionId, onImportCompleted) => {
  const connectionRef = useRef(null)
  const institutionIdRef = useRef(institutionId)

  // Atualiza a ref quando institutionId muda
  useEffect(() => {
    institutionIdRef.current = institutionId
  }, [institutionId])

  const connect = useCallback(async () => {
    // Não conecta se não tem institutionId ou token
    const token = getCookie("token")
    if (!institutionId || !token) return

    // Se já tem conexão ativa, desconecta antes
    if (connectionRef.current) {
      try {
        await connectionRef.current.stop()
      } catch {
        // ignora erros ao parar conexão anterior
      }
    }

    const hubUrl = `${getBaseUrl()}/hubs/import-notifications`

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(LogLevel.Warning)
      .build()

    connection.on("ImportCompleted", (data) => {
      const toastId = `import-${data.correlationId}`

      if (data.success) {
        toast.success(data.message, { autoClose: 8000, toastId })
      } else {
        toast.error(data.message, { autoClose: false, toastId })
      }

      if (onImportCompleted) {
        onImportCompleted(data)
      }
    })

    // Ao reconectar, re-junta ao grupo
    connection.onreconnected(async () => {
      try {
        if (institutionIdRef.current) {
          await connection.invoke("JoinInstitutionGroup", institutionIdRef.current)
        }
      } catch {
        // ignora erros ao re-juntar grupo
      }
    })

    try {
      await connection.start()
      await connection.invoke("JoinInstitutionGroup", institutionId)
      connectionRef.current = connection
    } catch (err) {
      console.warn("Não foi possível conectar ao SignalR:", err.message)
    }
  }, [institutionId, onImportCompleted])

  // Conecta quando institutionId muda
  useEffect(() => {
    connect()

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {})
        connectionRef.current = null
      }
    }
  }, [connect])
}

export default useImportNotifications
